(() => {
  const ROOT_SELECTOR = '[data-strapless-product-page]';
  const initializedRoots = new WeakSet();
  const cleanupMap = new WeakMap();
  const STORAGE_KEY = 'strapless.bundleSelections.v3';

  function injectBundlePolishStyles() {
    if (document.querySelector('[data-strapless-bundle-polish]')) return;
    const style = document.createElement('style');
    style.dataset.straplessBundlePolish = 'true';
    style.textContent = `
      .strapless-product-page .sp-variant-row { gap: 12px !important; padding: 12px !important; }
      .strapless-product-page .sp-variant-controls { gap: 12px !important; }
      .strapless-product-page .sp-variant-field label { margin-bottom: 7px !important; font-size: 9px !important; line-height: 1.15; }
      .strapless-product-page .sp-variant-field select {
        width: 100%; height: 44px !important; min-height: 44px; padding: 0 40px 0 14px !important;
        border: 1px solid #d2cfca !important; border-radius: 11px !important; background-color: #fff !important;
        color: var(--sp-ink) !important; box-shadow: none !important; font-size: 13px !important; line-height: 1.2;
      }
      .strapless-product-page .sp-variant-field select:hover { border-color: #bcb7b2 !important; }
      .strapless-product-page .sp-variant-field select:focus,
      .strapless-product-page .sp-variant-field select:focus-visible {
        outline: none !important; border-color: var(--sp-accent-strong) !important;
        box-shadow: 0 0 0 2px color-mix(in srgb, var(--sp-accent) 28%, transparent) !important;
      }
      @media (max-width: 749px) {
        .strapless-product-page .sp-variant-row { gap: 9px !important; padding: 9px !important; }
        .strapless-product-page .sp-variant-controls { gap: 8px !important; }
        .strapless-product-page .sp-variant-field select {
          height: 42px !important; min-height: 42px; padding: 0 34px 0 11px !important;
          border-radius: 10px !important; font-size: 12px !important;
        }
      }
    `;
    document.head.appendChild(style);
  }

  function parseJSONScript(root, selector) {
    const el = root.querySelector(selector);
    if (!el) return [];
    try { return JSON.parse(el.textContent || '[]'); }
    catch (error) { console.error('Strapless: invalid JSON payload', error); return []; }
  }

  function moneyFormatter(root) {
    const currency = root.dataset.currency || window.Shopify?.currency?.active || 'USD';
    const locale = document.documentElement.lang || navigator.language || 'en-US';
    try { return new Intl.NumberFormat(locale, { style: 'currency', currency }); }
    catch (_) { return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }); }
  }

  function variantImage(variant, fallback) {
    return variant?.featured_image?.src || variant?.featured_image || fallback || '';
  }

  function exactVariant(variants, options) {
    return variants.find((variant) => {
      const values = Array.isArray(variant.options) ? variant.options : [];
      return values.length === options.length && values.every((value, index) => value === options[index]);
    });
  }

  function possibleVariant(variants, selections, changingIndex, candidateValue) {
    return variants.some((variant) => {
      if (!variant.available) return false;
      const values = Array.isArray(variant.options) ? variant.options : [];
      return values.every((value, index) => {
        if (index === changingIndex) return value === candidateValue;
        const selected = selections[index];
        return !selected || value === selected;
      });
    });
  }

  function setupCountdown(root) {
    const minutesEl = root.querySelector('[data-sale-minutes]');
    const secondsEl = root.querySelector('[data-sale-seconds]');
    if (!minutesEl || !secondsEl) return () => {};
    const duration = Math.max(60, Number(root.dataset.saleDuration || 900));
    let remaining = duration;
    const paint = () => {
      minutesEl.textContent = Math.floor(remaining / 60).toString().padStart(2, '0');
      secondsEl.textContent = (remaining % 60).toString().padStart(2, '0');
      remaining = remaining > 0 ? remaining - 1 : duration;
    };
    paint();
    const timer = window.setInterval(paint, 1000);
    return () => window.clearInterval(timer);
  }

  function setupFAQ(root) {
    const details = [...root.querySelectorAll('.sp-faq-item')];
    const handlers = [];
    details.forEach((item) => {
      const handler = () => {
        if (!item.open) return;
        details.forEach((other) => { if (other !== item) other.open = false; });
      };
      item.addEventListener('toggle', handler);
      handlers.push(() => item.removeEventListener('toggle', handler));
    });
    return () => handlers.forEach((fn) => fn());
  }

  function setupProductForm(root) {
    const variants = parseJSONScript(root, '[data-product-variants-json]');
    const rowTemplate = root.querySelector('[data-variant-row-template]');
    const variantList = root.querySelector('[data-variant-list]');
    const offerInputs = [...root.querySelectorAll('[data-offer-input]')];
    const offerCards = [...root.querySelectorAll('[data-offer-card]')];
    const errorEl = root.querySelector('[data-form-error]');
    const giftCard = root.querySelector('[data-gift-card]');
    const fallbackImage = root.dataset.fallbackImage || '';
    if (!variants.length || !rowTemplate || !variantList || !offerInputs.length) return () => {};

    const formatter = moneyFormatter(root);
    const initialVariant = variants.find((variant) => Number(variant.id) === Number(root.dataset.initialVariantId))
      || variants.find((variant) => variant.available)
      || variants[0];
    const initialOptions = Array.isArray(initialVariant?.options) ? [...initialVariant.options] : [];
    const productKey = String(root.dataset.initialVariantId || 'strapless').split('-')[0];

    const offerConfig = (value) => {
      const input = offerInputs.find((item) => item.value === value) || offerInputs[0];
      const count = Math.max(1, Number(input?.dataset.count || 1));
      const paidCount = Math.max(1, Number(input?.dataset.paidCount || 1));
      const requiresDiscount = input?.dataset.requiresDiscount === 'true';
      return {
        count,
        paidCount,
        freeCount: Math.max(0, count - paidCount),
        includeGift: requiresDiscount && count > paidCount
      };
    };

    const readSaved = () => {
      try {
        const all = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
        return all?.[productKey] && typeof all[productKey] === 'object' ? all[productKey] : {};
      } catch (_) { return {}; }
    };

    let state = readSaved();
    state.offers ||= {};
    let selectedOffer = offerInputs.some((input) => input.value === state.selectedOffer)
      ? state.selectedOffer
      : (offerInputs.find((input) => input.checked)?.value || offerInputs[0].value);

    const getOfferState = (value) => {
      const config = offerConfig(value);
      const existing = state.offers[value] || {};
      let selections = Array.isArray(existing.selections) ? existing.selections : [];
      selections = Array.from({ length: config.count }, (_, index) => {
        const candidate = Array.isArray(selections[index]) ? selections[index] : initialOptions;
        const exact = exactVariant(variants, candidate);
        return exact ? [...candidate] : [...initialOptions];
      });
      state.offers[value] = {
        selections,
        seedUsed: existing.seedUsed === true
      };
      return state.offers[value];
    };

    const saveState = () => {
      state.selectedOffer = selectedOffer;
      try {
        const all = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
        all[productKey] = state;
        localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
      } catch (_) {}
    };

    const showError = (message = '') => {
      if (!errorEl) return;
      errorEl.textContent = message;
      errorEl.hidden = !message;
    };

    const rowImage = (row, variant) => {
      const img = row.querySelector('[data-row-image]');
      if (!img) return;
      const src = variantImage(variant, fallbackImage);
      if (src) img.src = src;
      img.alt = variant?.title ? `Selected product variant: ${variant.title}` : 'Selected product variant';
    };

    // The dedicated pre-launch QA section is the single owner of option availability.
    // This base runtime only reflects whether the current exact combination exists.
    const refreshAvailability = (row, rowIndex, offerState) => {
      const current = offerState.selections[rowIndex] || initialOptions;
      const variant = exactVariant(variants, current);
      row.classList.toggle('is-unavailable', !variant?.available);
      rowImage(row, variant || initialVariant);
    };

    const isSizeSelect = (select) => /\bsize\b/i.test(select?.getAttribute('aria-label') || '');

    const propagateFirstSizeOnce = (sourceSelect, offerState, config) => {
      if (offerState.seedUsed || !isSizeSelect(sourceSelect)) return false;
      const optionIndex = Number(sourceSelect.dataset.optionIndex);
      if (!Number.isInteger(optionIndex)) return false;
      const desired = sourceSelect.value;

      for (let rowIndex = 1; rowIndex < config.count; rowIndex += 1) {
        const current = offerState.selections[rowIndex] || [...initialOptions];
        if (!possibleVariant(variants, current, optionIndex, desired)) continue;
        const next = [...current];
        next[optionIndex] = desired;
        offerState.selections[rowIndex] = next;
      }
      offerState.seedUsed = true;
      return true;
    };

    const updateOfferPrices = () => {
      offerCards.forEach((card) => {
        const input = card.querySelector('[data-offer-input]');
        if (!input) return;
        const config = offerConfig(input.value);
        const base = Number(initialVariant?.price || 0);
        const priceEl = card.querySelector('[data-offer-price]');
        const compareEl = card.querySelector('[data-offer-compare]');
        if (priceEl) priceEl.textContent = formatter.format((base * config.paidCount) / 100);
        if (compareEl) {
          compareEl.textContent = config.freeCount ? formatter.format((base * config.count) / 100) : '';
          compareEl.hidden = !config.freeCount;
        }
      });
    };

    const decorateOfferCards = () => {
      offerCards.forEach((card) => {
        const input = card.querySelector('[data-offer-input]');
        const name = card.querySelector('.sp-offer-name');
        if (!input || !name) return;
        const config = offerConfig(input.value);
        let bonus = card.querySelector('.sp-offer-bonus-line');
        if (!config.includeGift) { bonus?.remove(); return; }
        if (!bonus) {
          bonus = document.createElement('span');
          bonus.className = 'sp-offer-bonus-line';
          name.appendChild(bonus);
        }
        bonus.innerHTML = `<b>${config.freeCount} FREE TOP${config.freeCount === 1 ? '' : 'S'}</b><b>+ FREE EBOOK</b>`;
      });
    };

    const prepareGiftCard = () => {
      if (!giftCard) return;
      giftCard.classList.add('is-included');
      giftCard.hidden = true;
      if (!giftCard.querySelector('[data-auto-gift-status]')) {
        const status = document.createElement('span');
        status.className = 'sp-auto-gift-status';
        status.dataset.autoGiftStatus = 'true';
        status.textContent = 'INCLUDED ✓';
        giftCard.appendChild(status);
      }
    };

    const renderRows = () => {
      const config = offerConfig(selectedOffer);
      const offerState = getOfferState(selectedOffer);
      variantList.replaceChildren();

      for (let rowIndex = 0; rowIndex < config.count; rowIndex += 1) {
        const fragment = rowTemplate.content.cloneNode(true);
        const row = fragment.querySelector('[data-variant-row]');
        if (!row) continue;
        row.dataset.rowIndex = String(rowIndex);
        row.querySelectorAll('[data-row-number]').forEach((el) => { el.textContent = String(rowIndex + 1); });

        const selects = [...row.querySelectorAll('[data-option-index]')];
        selects.forEach((select, optionIndex) => {
          const desired = offerState.selections[rowIndex]?.[optionIndex];
          if (desired && [...select.options].some((option) => option.value === desired)) select.value = desired;
          select.addEventListener('change', () => {
            offerState.selections[rowIndex] = selects.map((item) => item.value);

            if (rowIndex > 0 && !offerState.seedUsed) offerState.seedUsed = true;

            const seeded = rowIndex === 0
              ? propagateFirstSizeOnce(select, offerState, config)
              : false;

            saveState();
            if (seeded) renderRows();
            else refreshAvailability(row, rowIndex, offerState);
            showError('');
          });
        });

        if (rowIndex >= config.paidCount) {
          row.classList.add('is-free-item');
          const freePill = document.createElement('span');
          freePill.className = 'sp-free-item-pill';
          freePill.textContent = 'FREE';
          row.appendChild(freePill);
        }

        variantList.appendChild(fragment);
        const inserted = variantList.lastElementChild;
        if (inserted) refreshAvailability(inserted, rowIndex, offerState);
      }

      if (giftCard) {
        variantList.appendChild(giftCard);
        giftCard.hidden = !config.includeGift;
      }
    };

    const positionSelectedDetails = () => {
      const selectedCard = offerCards.find((card) => card.querySelector('[data-offer-input]')?.value === selectedOffer);
      if (selectedCard) selectedCard.insertAdjacentElement('afterend', variantList);
    };

    const setOffer = (value) => {
      if (!offerInputs.some((input) => input.value === value)) return;
      selectedOffer = value;
      state.selectedOffer = value;
      getOfferState(value);
      offerInputs.forEach((input) => { input.checked = input.value === value; });
      offerCards.forEach((card) => {
        const selected = card.querySelector('[data-offer-input]')?.value === value;
        card.classList.toggle('is-selected', selected);
        card.setAttribute('aria-expanded', selected ? 'true' : 'false');
      });
      renderRows();
      positionSelectedDetails();
      saveState();
      showError('');
    };

    prepareGiftCard();
    decorateOfferCards();
    updateOfferPrices();

    const offerHandlers = [];
    offerInputs.forEach((input) => {
      const handler = () => setOffer(input.value);
      input.addEventListener('change', handler);
      offerHandlers.push(() => input.removeEventListener('change', handler));
    });

    setOffer(selectedOffer);
    return () => offerHandlers.forEach((fn) => fn());
  }

  function initRoot(root) {
    if (!root || initializedRoots.has(root)) return;
    initializedRoots.add(root);
    injectBundlePolishStyles();
    // Gallery behavior is owned by strapless-media-controller to avoid duplicate handlers.
    const cleanups = [setupCountdown(root), setupFAQ(root), setupProductForm(root)];
    cleanupMap.set(root, () => cleanups.forEach((cleanup) => { try { cleanup?.(); } catch (_) {} }));
  }

  function init(scope = document) {
    if (scope.matches?.(ROOT_SELECTOR)) initRoot(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach(initRoot);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  else init(document);

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.matches?.(ROOT_SELECTOR) ? event.target : event.target.querySelector?.(ROOT_SELECTOR);
    cleanupMap.get(root)?.();
  });
})();
